/**
 * DeepAnalysisResultsPanel - Tier 2 Deep Analysis Results Display
 * ============================================================================
 * DC-1043: Panel component displaying Tier 2 LLM-powered deep analysis results
 *
 * Features:
 * - Regulation selector dropdown
 * - "Run Deep Analysis" button
 * - Compliance score gauge + level badge
 * - Summary stats: X met, Y partial, Z not met
 * - Requirement checklist (RequirementEvaluationCard[])
 * - Remediation plan section
 *
 * Pattern: Follows CredibilityGapsPanel.tsx (DC-933)
 *
 * @version 1.0.0
 * @created 2026-01-25
 * ============================================================================
 */

import React, { useState, useMemo, useCallback } from 'react';
import { ScoreGauge } from '../../vendor/ScoreGauge';
import { RequirementEvaluationCard } from './RequirementEvaluationCard';
import {
  type RegulationSchemaId,
  type RegulationSchemaInfo,
  type DeepAnalysisResult,
  type RequirementEvaluation,
  REGULATION_CONFIG,
  COMPLIANCE_LEVEL_CONFIG,
  EFFORT_CONFIG,
} from './types';
import './DeepAnalysisResultsPanel.css';

// ============================================================================
// Types
// ============================================================================

export interface DeepAnalysisResultsPanelProps {
  /** Document ID */
  documentId: string;
  /** Deep analysis result (null if not analyzed) */
  result: DeepAnalysisResult | null;
  /** Available regulations */
  availableRegulations: RegulationSchemaInfo[];
  /** Currently selected regulation */
  selectedRegulation: RegulationSchemaId | null;
  /** Callback when regulation changes */
  onRegulationChange: (regulationId: RegulationSchemaId) => void;
  /** Callback to run deep analysis */
  onRunAnalysis: (regulationId: RegulationSchemaId) => void;
  /** Loading state for fetching results */
  loading?: boolean;
  /** Whether analysis is running */
  isRunning?: boolean;
  /** Error message */
  error?: string | null;
  /** Custom className */
  className?: string;
}

// ============================================================================
// Helper Components
// ============================================================================

interface ComplianceLevelBadgeProps {
  level: DeepAnalysisResult['compliance_level'];
}

const ComplianceLevelBadge: React.FC<ComplianceLevelBadgeProps> = ({ level }) => {
  const config = COMPLIANCE_LEVEL_CONFIG[level];

  return (
    <span
      className="dap-compliance-badge"
      style={{ backgroundColor: config.bgColor, color: config.color }}
    >
      {config.label}
    </span>
  );
};

interface StatsSummaryProps {
  met: number;
  partial: number;
  notMet: number;
  total: number;
}

const StatsSummary: React.FC<StatsSummaryProps> = ({ met, partial, notMet, total }) => {
  return (
    <div className="dap-stats">
      <div className="dap-stat dap-stat--met">
        <span className="dap-stat__value">{met}</span>
        <span className="dap-stat__label">Met</span>
      </div>
      <div className="dap-stat dap-stat--partial">
        <span className="dap-stat__value">{partial}</span>
        <span className="dap-stat__label">Partial</span>
      </div>
      <div className="dap-stat dap-stat--not-met">
        <span className="dap-stat__value">{notMet}</span>
        <span className="dap-stat__label">Not Met</span>
      </div>
      <div className="dap-stat dap-stat--total">
        <span className="dap-stat__value">{total}</span>
        <span className="dap-stat__label">Total</span>
      </div>
    </div>
  );
};

interface RemediationPlanSectionProps {
  plan: DeepAnalysisResult['remediation_plan'];
}

const RemediationPlanSection: React.FC<RemediationPlanSectionProps> = ({ plan }) => {
  const effortConfig = EFFORT_CONFIG[plan.estimated_effort];

  return (
    <div className="dap-remediation">
      <div className="dap-remediation__header">
        <h4 className="dap-remediation__title">Remediation Plan</h4>
        <span className="dap-remediation__effort">
          Estimated Effort: <strong>{effortConfig.label}</strong>
          <span className="dap-remediation__dots">
            {[1, 2, 3].map(dot => (
              <span
                key={dot}
                className={`dap-remediation__dot ${
                  dot <= effortConfig.dots ? 'dap-remediation__dot--filled' : ''
                }`}
              />
            ))}
          </span>
        </span>
      </div>

      <p className="dap-remediation__summary">{plan.summary}</p>

      {plan.critical_gaps.length > 0 && (
        <div className="dap-remediation__critical">
          <h5 className="dap-remediation__critical-title">
            ⚠ Critical Gaps ({plan.critical_gaps.length})
          </h5>
          <ul className="dap-remediation__critical-list">
            {plan.critical_gaps.map((gap, idx) => (
              <li key={idx}>{gap}</li>
            ))}
          </ul>
        </div>
      )}

      {plan.actions.length > 0 && (
        <div className="dap-remediation__actions">
          <h5 className="dap-remediation__actions-title">Prioritized Actions</h5>
          <div className="dap-remediation__actions-list">
            {plan.actions.map((action, idx) => (
              <div key={idx} className="dap-action">
                <span className="dap-action__priority">{action.priority}</span>
                <div className="dap-action__content">
                  <span className="dap-action__title">{action.title}</span>
                  <span className="dap-action__description">{action.description}</span>
                  <div className="dap-action__meta">
                    <span className="dap-action__impact">
                      Impact: {action.expected_impact}
                    </span>
                    <span className="dap-action__effort">
                      Effort: {EFFORT_CONFIG[action.effort].label}
                    </span>
                    {action.suggested_owner && (
                      <span className="dap-action__owner">
                        Owner: {action.suggested_owner}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Loading State
// ============================================================================

const LoadingState: React.FC = () => (
  <div className="dap-panel dap-panel--loading">
    <div className="dap-loading">
      <div className="dap-loading__spinner" />
      <p className="dap-loading__text">Loading deep analysis results...</p>
    </div>
  </div>
);

// ============================================================================
// Running State
// ============================================================================

const RunningState: React.FC<{ regulationName: string }> = ({ regulationName }) => (
  <div className="dap-panel dap-panel--running">
    <div className="dap-running">
      <div className="dap-running__spinner" />
      <p className="dap-running__title">Running Deep Analysis</p>
      <p className="dap-running__subtitle">
        Evaluating document against {regulationName} requirements...
      </p>
      <p className="dap-running__hint">This may take 10-30 seconds.</p>
    </div>
  </div>
);

// ============================================================================
// Empty State
// ============================================================================

interface EmptyStateProps {
  selectedRegulation: RegulationSchemaId | null;
  onRunAnalysis: () => void;
  disabled: boolean;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  selectedRegulation,
  onRunAnalysis,
  disabled,
}) => (
  <div className="dap-empty">
    <svg
      className="dap-empty__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z"
      />
    </svg>
    <p className="dap-empty__text">No deep analysis performed yet</p>
    <p className="dap-empty__subtext">
      {selectedRegulation
        ? 'Click the button below to analyze this document against the selected regulation.'
        : 'Select a regulation above to begin deep analysis.'}
    </p>
    {selectedRegulation && (
      <button
        className="dap-empty__button"
        onClick={onRunAnalysis}
        disabled={disabled}
      >
        Run Deep Analysis
      </button>
    )}
  </div>
);

// ============================================================================
// Main Component
// ============================================================================

export const DeepAnalysisResultsPanel: React.FC<DeepAnalysisResultsPanelProps> = ({
  documentId: _documentId,
  result,
  availableRegulations,
  selectedRegulation,
  onRegulationChange,
  onRunAnalysis,
  loading = false,
  isRunning = false,
  error = null,
  className = '',
}) => {
  const [expandedRequirement, setExpandedRequirement] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<RequirementEvaluation['status'] | 'all'>('all');

  // Get currently selected regulation info
  const selectedRegInfo = useMemo(() => {
    if (!selectedRegulation) return null;
    return (
      availableRegulations.find(r => r.id === selectedRegulation) ||
      REGULATION_CONFIG[selectedRegulation]
    );
  }, [selectedRegulation, availableRegulations]);

  // Filter requirements
  const filteredRequirements = useMemo(() => {
    if (!result) return [];
    if (filterStatus === 'all') return result.requirement_evaluations;
    return result.requirement_evaluations.filter(r => r.status === filterStatus);
  }, [result, filterStatus]);

  // Count by status
  const statusCounts = useMemo(() => {
    if (!result) return { met: 0, partial: 0, not_met: 0, not_applicable: 0 };
    const counts = { met: 0, partial: 0, not_met: 0, not_applicable: 0 };
    for (const req of result.requirement_evaluations) {
      counts[req.status]++;
    }
    return counts;
  }, [result]);

  const handleRunAnalysis = useCallback(() => {
    if (selectedRegulation) {
      onRunAnalysis(selectedRegulation);
    }
  }, [selectedRegulation, onRunAnalysis]);

  const handleToggleRequirement = useCallback((reqId: string) => {
    setExpandedRequirement(prev => (prev === reqId ? null : reqId));
  }, []);

  // Loading state
  if (loading) {
    return <LoadingState />;
  }

  // Running state
  if (isRunning) {
    return (
      <RunningState
        regulationName={selectedRegInfo?.name || selectedRegulation || 'regulation'}
      />
    );
  }

  return (
    <div className={`dap-panel ${className}`}>
      {/* Header with regulation selector */}
      <div className="dap-panel__header">
        <div className="dap-regulation-selector">
          <label htmlFor="regulation-select" className="dap-regulation-selector__label">
            Regulation Schema
          </label>
          <select
            id="regulation-select"
            className="dap-regulation-selector__select"
            value={selectedRegulation || ''}
            onChange={e => onRegulationChange(e.target.value as RegulationSchemaId)}
            disabled={isRunning}
          >
            <option value="">Select a regulation...</option>
            {availableRegulations.map(reg => (
              <option key={reg.id} value={reg.id} disabled={!reg.isImplemented}>
                {reg.name} {!reg.isImplemented && '(Coming Soon)'}
              </option>
            ))}
          </select>
        </div>

        <button
          className="dap-run-button"
          onClick={handleRunAnalysis}
          disabled={!selectedRegulation || isRunning}
        >
          {result && result.regulation_id === selectedRegulation
            ? 'Re-run Analysis'
            : 'Run Deep Analysis'}
        </button>
      </div>

      {/* Error display */}
      {error && (
        <div className="dap-error">
          <span className="dap-error__icon">⚠</span>
          <span className="dap-error__message">{error}</span>
        </div>
      )}

      {/* Empty state */}
      {!result && !error && (
        <EmptyState
          selectedRegulation={selectedRegulation}
          onRunAnalysis={handleRunAnalysis}
          disabled={!selectedRegulation || isRunning}
        />
      )}

      {/* Results */}
      {result && (
        <>
          {/* Score Section */}
          <div className="dap-score-section">
            <div className="dap-score-section__gauge">
              <ScoreGauge
                score={result.compliance_score}
                size="lg"
                variant="full"
                showValue={true}
                showPercentSign={false}
                animated={true}
              />
            </div>
            <div className="dap-score-section__info">
              <ComplianceLevelBadge level={result.compliance_level} />
              <p className="dap-score-section__description">
                {COMPLIANCE_LEVEL_CONFIG[result.compliance_level].description}
              </p>
              <StatsSummary
                met={statusCounts.met}
                partial={statusCounts.partial}
                notMet={statusCounts.not_met}
                total={result.total_requirements}
              />
            </div>
          </div>

          {/* Requirements Filter */}
          <div className="dap-filter">
            <span className="dap-filter__label">Filter by status:</span>
            <div className="dap-filter__chips">
              <button
                className={`dap-filter__chip ${filterStatus === 'all' ? 'dap-filter__chip--active' : ''}`}
                onClick={() => setFilterStatus('all')}
              >
                All ({result.total_requirements})
              </button>
              <button
                className={`dap-filter__chip dap-filter__chip--met ${filterStatus === 'met' ? 'dap-filter__chip--active' : ''}`}
                onClick={() => setFilterStatus('met')}
                disabled={statusCounts.met === 0}
              >
                Met ({statusCounts.met})
              </button>
              <button
                className={`dap-filter__chip dap-filter__chip--partial ${filterStatus === 'partial' ? 'dap-filter__chip--active' : ''}`}
                onClick={() => setFilterStatus('partial')}
                disabled={statusCounts.partial === 0}
              >
                Partial ({statusCounts.partial})
              </button>
              <button
                className={`dap-filter__chip dap-filter__chip--not-met ${filterStatus === 'not_met' ? 'dap-filter__chip--active' : ''}`}
                onClick={() => setFilterStatus('not_met')}
                disabled={statusCounts.not_met === 0}
              >
                Not Met ({statusCounts.not_met})
              </button>
            </div>
          </div>

          {/* Requirements List */}
          <div className="dap-requirements">
            {filteredRequirements.length === 0 ? (
              <p className="dap-requirements__empty">No requirements match the selected filter.</p>
            ) : (
              filteredRequirements.map(req => (
                <RequirementEvaluationCard
                  key={req.requirement_id}
                  evaluation={req}
                  regulationId={result.regulation_id}
                  expanded={expandedRequirement === req.requirement_id}
                  onToggle={() => handleToggleRequirement(req.requirement_id)}
                />
              ))
            )}
          </div>

          {/* Remediation Plan */}
          {result.remediation_plan && (
            <RemediationPlanSection plan={result.remediation_plan} />
          )}

          {/* Footer with metadata */}
          <div className="dap-panel__footer">
            <span className="dap-panel__timestamp">
              Analyzed: {new Date(result.analyzed_at).toLocaleString()}
            </span>
            <span className="dap-panel__model">Model: {result.model_used}</span>
          </div>
        </>
      )}
    </div>
  );
};

export default DeepAnalysisResultsPanel;
